import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { EpisodicBadge } from "components/EpisodicBadge";
import { isSpentOnArrival } from "encamp-shared/src/waste/formatMoveWindow";
import {
  AccumulationLimitType,
  ClockStatus,
  GetWasteContainerQuery,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { reliefCaveatShown } from "../EpisodicEvents/episodicDisplay";
import { deriveRuleSource } from "./clockRuleSource";
import {
  approachingNote,
  formatContainerDate,
  formatContainerDateLong,
  formatDaysRemaining,
  NO_VALUE_PLACEHOLDER,
} from "./containerDisplay";
import { EmptySectionState, SectionCard } from "./containerSections";
import { WasteContainerClockChip } from "./WasteContainerClockChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type AccumulationLimitRow = WasteContainerDetailData["clock"]["limits"][number];

/**
 * The engine's own verdict that this container's clock could not be
 * determined, with which generator-status axes it is waiting on. The
 * resolver only sets it while the clock actually runs (placed, not archived,
 * not shipped), and never infers it from an empty limits list — a resolved
 * VSQG facility legitimately produces zero limits too (VSQG central has no
 * federal TIME rule; VSQG satellite is exempt from the SAA volume limit).
 */
function undeterminedCallout(container: WasteContainerDetailData) {
  return container.clock.undetermined ?? null;
}

/**
 * Which rulebook a limit comes from. Episodic gets the shared badge, spelled
 * out — it is the only source whose name is the whole label, now that the
 * limit beside it reads just "60 days" — carrying the relief advisory the
 * container list shows for the same event.
 */
function RuleSourceBadge({
  limit,
  reliefUnavailable,
}: {
  limit: AccumulationLimitRow;
  reliefUnavailable: boolean;
}) {
  const source = deriveRuleSource(limit);
  if (source.isEpisodic) {
    return <EpisodicBadge density="long" reliefUnavailable={reliefUnavailable} />;
  }
  return <Chip size="small" variant="outlined" color={source.color} label={source.label} />;
}

function AccumulationLimitRows({
  limits,
  reliefUnavailable,
}: {
  limits: AccumulationLimitRow[];
  reliefUnavailable: boolean;
}) {
  return (
    <Stack spacing={1.5}>
      {limits.map((limit, index) => (
        <AccumulationLimitRow
          key={`${limit.limitType}-${index}`}
          limit={limit}
          reliefUnavailable={reliefUnavailable}
        />
      ))}
    </Stack>
  );
}

/**
 * One non-governing limit, stacked rather than tabulated.
 *
 * A six-column table was the obvious shape and the wrong one: these cards are
 * half the drawer wide, and a volume limit has no deadline and no days
 * remaining, so two columns sat empty while `basis` wrapped a word per line and
 * the status chip pushed past the card's edge. Stacking lets each limit say
 * only what it has, in the same order the dial above says it.
 */
function AccumulationLimitRow({
  limit,
  reliefUnavailable,
}: {
  limit: AccumulationLimitRow;
  reliefUnavailable: boolean;
}) {
  const timing = [
    limit.deadlineDate ? `Due ${formatContainerDate(limit.deadlineDate)}` : null,
    // A window with no duration says its own rule in `basis`, so a countdown
    // here would only contradict it with "0 days left". Lateness still counts.
    limit.daysRemaining != null && !isSpentOnArrival(limit.totalDays, limit.daysRemaining)
      ? formatDaysRemaining(limit.daysRemaining)
      : null,
  ].filter(Boolean);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2">{limit.basis}</Typography>
        <RuleSourceBadge limit={limit} reliefUnavailable={reliefUnavailable} />
        <WasteContainerClockChip status={limit.status} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {[limit.citation, ...timing].filter(Boolean).join(" · ")}
      </Typography>
    </Box>
  );
}

/**
 * How much of a window has been spent, clamped to the ring's 0..1. Null when
 * there is nothing to divide by — no length, or no reading to divide.
 */
function elapsedFraction(total: number | null, used: number | null): number | null {
  if (!total || used === null) {
    return null;
  }
  return Math.max(0, Math.min(1, used / total));
}

/**
 * The governing time limit as a ring: how much of the window is gone, and how
 * much is left in the middle.
 *
 * Only the soonest dated TIME limit gets the ring — the one actually driving
 * the deadline. Everything else the engine emits is a coinciding obligation
 * (the volume cap, its move window, a state's own satellite clock) and lists
 * underneath: `clock.limits` carries only limits that APPLY — a superseded
 * residence clock (episodic vs standing) is resolved engine-side, and a
 * federal-plus-state pair cannot occur.
 */
function AccumulationClockDial({
  limit,
  reliefUnavailable,
}: {
  limit: AccumulationLimitRow;
  reliefUnavailable: boolean;
}) {
  const theme = useTheme();
  const total = limit.totalDays ?? null;
  const remaining = limit.daysRemaining ?? null;
  const used = total !== null && remaining !== null ? Math.min(total, total - remaining) : null;
  const overdue = remaining !== null && remaining < 0;
  // A satellite move window whose rules grant no time past the crossing
  // (Delaware's same-shift rule). Counting down from zero would read as a
  // deadline that happens to be close rather than one with no duration at all.
  // `totalDays === 0` identifies it without a flag of its own: every other
  // limit the engine builds carries a real length (90/180/270/365 for a
  // residence clock, 60 for an episodic window) or none at all (a volume cap).
  const zeroLengthWindow = total === 0;
  // Overdue keeps the ring full rather than wrapping past it, which would read
  // as progress starting over. A window with no duration is spent the instant
  // it opens, so its ring is full from the start — without this it would
  // divide by zero and draw nothing, leaving an overdue container grey.
  const fraction = zeroLengthWindow ? 1 : elapsedFraction(total, used);
  // Only lateness is colored. Approaching used to be amber, but the window
  // opens 30 days out — long enough that most running containers wore a
  // warning color, which left nothing louder for the ones actually late. The
  // count is the signal; `approachingNote` says on hover what being inside
  // the window means.
  const ringColor = overdue ? theme.palette.error.main : theme.palette.text.primary;
  const note = approachingNote(limit.status, overdue, limit.basis);

  return (
    <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box sx={{ position: "relative", width: 148, height: 148, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={148}
          thickness={4}
          sx={{ color: "grey.300", position: "absolute", inset: 0 }}
        />
        {fraction !== null && (
          <CircularProgress
            variant="determinate"
            value={fraction * 100}
            size={148}
            thickness={4}
            sx={{ color: ringColor, position: "absolute", inset: 0 }}
          />
        )}
        <Tooltip title={note ?? ""}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ position: "absolute", inset: 0, textAlign: "center" }}
          >
            {isSpentOnArrival(total, remaining) ? (
              <Typography variant="subtitle1" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
                End of shift
              </Typography>
            ) : (
              <>
                <Typography variant="h5" sx={{ lineHeight: 1, fontWeight: 600 }}>
                  {remaining === null ? NO_VALUE_PLACEHOLDER : Math.abs(remaining)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {remaining === null ? "" : overdue ? "days overdue" : "days left"}
                </Typography>
              </>
            )}
          </Stack>
        </Tooltip>
      </Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Deadline
          </Typography>
          <Typography variant="body2">{formatContainerDateLong(limit.deadlineDate)}</Typography>
        </Box>
        {/* Omitted for a window with no duration: there is no span to report
            progress against, and "0 of 0 days" reads the same whether the
            window is still open or long blown. The deadline above and the
            dial's own center carry that state instead. */}
        {!zeroLengthWindow && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              On site
            </Typography>
            <Typography variant="body2">
              {used !== null && total !== null
                ? `${used} of ${total} days`
                : formatDaysRemaining(limit.daysRemaining)}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Basis
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{limit.basis}</Typography>
            <RuleSourceBadge limit={limit} reliefUnavailable={reliefUnavailable} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {limit.citation}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

/** Why a volume limit reads "Not determined". No link: the verdict is the
 * AREA's, and the unevaluable member may not be this container's stream. */
function UndeterminedVolumeAlert() {
  return (
    <Alert severity="warning">
      The volume limit can&apos;t be checked — waste in this area is recorded by weight, and
      converting weight to gallons needs the waste stream&apos;s specific gravity. Add one on the
      waste stream&apos;s profile.
    </Alert>
  );
}

export function AccumulationClockSection({ container }: { container: WasteContainerDetailData }) {
  const reliefUnavailable =
    container.episodicEvent !== null &&
    container.episodicEvent !== undefined &&
    reliefCaveatShown(container.episodicEvent.relief);
  const { clock } = container;

  const volumeUndetermined = clock.limits.some(
    (limit) => limit.status === ClockStatus.Undetermined
  );

  let body: ReactNode;
  // The soonest dated limit governs; it gets the ring, and the coinciding
  // rest stay listed (see AccumulationClockDial's doc).
  const timeLimits = clock.limits
    .filter((limit) => limit.limitType === AccumulationLimitType.Time && limit.deadlineDate)
    .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0));
  const governing = timeLimits[0];
  const others = clock.limits.filter((limit) => limit !== governing);
  const undetermined = undeterminedCallout(container);
  if (governing) {
    body = (
      <Stack spacing={3}>
        <AccumulationClockDial limit={governing} reliefUnavailable={reliefUnavailable} />
        {others.length > 0 && (
          <AccumulationLimitRows limits={others} reliefUnavailable={reliefUnavailable} />
        )}
        {volumeUndetermined && <UndeterminedVolumeAlert />}
      </Stack>
    );
  } else if (clock.limits.length > 0) {
    body = (
      <Stack spacing={3}>
        <AccumulationLimitRows limits={clock.limits} reliefUnavailable={reliefUnavailable} />
        {volumeUndetermined && <UndeterminedVolumeAlert />}
      </Stack>
    );
  } else if (undetermined) {
    // Warning, not info: an undetermined clock means a real deadline may be
    // running unseen. The reason is the engine's own composition — the rules
    // that declined to answer say what they were missing — rendered as given.
    body = <Alert severity="warning">{undetermined.reason}</Alert>;
  } else {
    body = <EmptySectionState message="No accumulation clock applies to this container." />;
  }

  // No compliance badge here: the ring already says the deadline has passed,
  // in red, with the number of days on it. A chip repeating that was the
  // loudest thing on the card and the specs don't draw one. The findings
  // themselves still surface on the compliance dashboard.
  return <SectionCard title="Accumulation clock">{body}</SectionCard>;
}
